import { createRng } from './rng';
import type { SampleInvoice } from './invoices';

/**
 * Spend / supplier intelligence aggregator — used by:
 *   - Customer Portal /spend page (supplier intelligence)
 *   - Customer Portal HomePage "Top suppliers" widget
 *   - Admin Console /tickstar "Analytics SLA" tab
 *
 * The Solara supplier intelligence module surfaces supplier-level signals
 * (cycle-time, on-time-payment rate, AED concentration). We aggregate
 * the local SampleInvoice[] into the same view so the UI lights up
 * even before the partner endpoint is wired.
 */

export interface SpendByCategory {
  /** UNSPSC family or a friendly label. */
  category: string;
  /** Localised label. */
  labelEn: string;
  labelAr: string;
  spendAed: number;
  invoiceCount: number;
  /** Share of total spend, 0..1. */
  share: number;
  /** Trend vs. previous month: positive percent = increasing. */
  changePct: number;
}

export interface SupplierIntelligence {
  supplier: string;
  supplierAr: string;
  trn: string;
  spendAed: number;
  invoiceCount: number;
  /** Average cycle time from receipt → AP (days). */
  cycleDays: number;
  /** On-time payment rate, 0..1. */
  onTimePaymentRate: number;
  /** Late-TDD rate from this supplier (red flag if > 5%). */
  lateTddRate: number;
  /** Recovery-audit findings on this supplier (count). */
  auditFindings: number;
  /** Risk score 1..5. */
  risk: 1 | 2 | 3 | 4 | 5;
}

export interface SpendOverview {
  /** Total YTD spend in AED. */
  totalSpendAed: number;
  /** Distinct suppliers with at least 1 invoice this period. */
  supplierCount: number;
  /** YTD invoice count. */
  invoiceCount: number;
  /** Average invoice value AED. */
  avgInvoiceAed: number;
  /** Concentration: top-5 suppliers as share of total spend (0..1). */
  top5Concentration: number;
  /** 12-month spend series (oldest first). */
  monthlySeries: { month: string; aed: number }[];
  byCategory: SpendByCategory[];
  topSuppliers: SupplierIntelligence[];
}

const CATEGORIES: { key: string; en: string; ar: string }[] = [
  { key: 'IT services & SaaS',         en: 'IT services & SaaS',         ar: 'خدمات تكنولوجيا المعلومات والـ SaaS' },
  { key: 'Logistics & freight',        en: 'Logistics & freight',        ar: 'الخدمات اللوجستية والشحن' },
  { key: 'Professional services',      en: 'Professional services',      ar: 'الخدمات المهنية' },
  { key: 'Marketing & advertising',    en: 'Marketing & advertising',    ar: 'التسويق والإعلان' },
  { key: 'Facilities & maintenance',   en: 'Facilities & maintenance',   ar: 'المرافق والصيانة' },
  { key: 'Hospitality & travel',       en: 'Hospitality & travel',       ar: 'الضيافة والسفر' },
  { key: 'Office supplies',            en: 'Office supplies',            ar: 'مستلزمات المكتب' },
  { key: 'Power, fuel, utilities',     en: 'Power, fuel, utilities',     ar: 'الكهرباء والوقود والمرافق' },
];

const SUPPLIERS: { en: string; ar: string; trn: string }[] = [
  { en: 'Etisalat by e&',                   ar: 'اتصالات من إي&',                       trn: '100474123100003' },
  { en: 'DEWA',                              ar: 'هيئة كهرباء ومياه دبي',                 trn: '100129911100003' },
  { en: 'Al Futtaim Logistics LLC',          ar: 'الفطيم للخدمات اللوجستية ذ.م.م',          trn: '100392827100003' },
  { en: 'Sharaf DG LLC',                     ar: 'شرف دي جي ذ.م.م',                       trn: '100474201100003' },
  { en: 'Aldar Engineering Services',        ar: 'الدار للخدمات الهندسية',                  trn: '100501322100003' },
  { en: 'Tristar Engineering FZE',           ar: 'تريستار للهندسة المنطقة الحرة',           trn: '100442761100003' },
  { en: 'Spinneys Dubai',                    ar: 'سبينيس دبي',                            trn: '100337612100003' },
  { en: 'Mashreq Facilities Management',     ar: 'المشرق لإدارة المرافق',                  trn: '100513004100003' },
  { en: 'Damac Maintenance Services',        ar: 'داماك لخدمات الصيانة',                   trn: '100428176100003' },
  { en: 'Lulu Hypermarket FZE',              ar: 'لولو هايبرماركت المنطقة الحرة',          trn: '100311118100003' },
  { en: 'Al Tayer Group',                    ar: 'مجموعة الطاير',                          trn: '100491872100003' },
  { en: 'Apparel Group LLC',                 ar: 'مجموعة الملابس ذ.م.م',                   trn: '100278322100003' },
];

export function generateSpendOverview(invoices: SampleInvoice[], seed = 0x5_d_77): SpendOverview {
  const rng = createRng(seed);
  const out = invoices.filter((i) => i.direction === 'OUTBOUND' || i.direction === 'INBOUND');

  const totalSpendAed = out.reduce((s, i) => s + i.totalGrossAmount, 0) || rng.int(2_400_000, 6_500_000);
  const supplierCount = SUPPLIERS.length;
  const invoiceCount = out.length || rng.int(180, 420);
  const avgInvoiceAed = invoiceCount > 0 ? Math.round(totalSpendAed / invoiceCount) : 0;

  const monthLabels = [
    'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov',
    'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May',
  ];
  const monthlySeries = monthLabels.map((m) => ({
    month: m,
    aed: Math.round(totalSpendAed / 12 * (0.55 + rng.next() * 0.95)),
  }));

  // ---- By category ------------------------------------------------
  // Generate weighted random splits.
  const weights = CATEGORIES.map(() => rng.int(8, 24));
  const wSum = weights.reduce((s, w) => s + w, 0);
  const byCategory: SpendByCategory[] = CATEGORIES.map((c, idx) => {
    const share = weights[idx]! / wSum;
    const spend = Math.round(totalSpendAed * share);
    return {
      category: c.key,
      labelEn: c.en,
      labelAr: c.ar,
      spendAed: spend,
      invoiceCount: Math.max(1, Math.round(invoiceCount * share)),
      share,
      changePct: Number(((rng.next() - 0.5) * 0.4).toFixed(3)),
    };
  }).sort((a, b) => b.spendAed - a.spendAed);

  // ---- Top suppliers ---------------------------------------------
  const supplierShares = SUPPLIERS.map(() => rng.next() + 0.2);
  const sShareSum = supplierShares.reduce((s, w) => s + w, 0);
  const topSuppliers: SupplierIntelligence[] = SUPPLIERS.map((s, idx) => {
    const share = supplierShares[idx]! / sShareSum;
    const spend = Math.round(totalSpendAed * share);
    const findings = rng.int(0, 4);
    return {
      supplier: s.en,
      supplierAr: s.ar,
      trn: s.trn,
      spendAed: spend,
      invoiceCount: Math.max(1, Math.round(invoiceCount * share)),
      cycleDays: rng.int(4, 28),
      onTimePaymentRate: Number((0.78 + rng.next() * 0.21).toFixed(3)),
      lateTddRate: Number((rng.next() * 0.07).toFixed(3)),
      auditFindings: findings,
      risk: ((findings >= 3 ? 4 : findings >= 1 ? 2 : 1) + (rng.chance(0.2) ? 1 : 0)) as 1 | 2 | 3 | 4 | 5,
    };
  }).sort((a, b) => b.spendAed - a.spendAed);

  const top5Concentration = topSuppliers
    .slice(0, 5)
    .reduce((s, x) => s + x.spendAed, 0) / Math.max(1, totalSpendAed);

  return {
    totalSpendAed,
    supplierCount,
    invoiceCount,
    avgInvoiceAed,
    top5Concentration: Number(top5Concentration.toFixed(3)),
    monthlySeries,
    byCategory,
    topSuppliers,
  };
}
